/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CostCenterFinderListItem, FormFlowState } from '@gh/builder-shared';
import {
  CheckUsageDto,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskType,
  ScheduleTemplateDetailsDto,
  ScheduleTemplateListItemDto,
  ScheduleTemplateStatus,
  ScheduleTemplateTaskListItemDto,
  ScheduleType,
  TaskRelationDto,
} from '@gh/builder-shared-data';
import {
  EntityConfig,
  entityValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  HelpID,
} from '@gh/common-shared';
import { ID, WithDirty, WithDirtyType, WithFormMode, WithVersion } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { newTime, shiftDays, shiftHours } from '@gh/core-util';
import { GenericScheduleTaskInfo, recalculateScheduleTasks } from '@gh/builder-shared-gantt-chart';

import { find, get, map, omit, pick } from 'lodash';

export const DAY_HOURS = 8;
export const START_HOUR = 8;

export type ScheduleTemplateListItem = ScheduleTemplateListItemDto;
export type ScheduleTemplateTask = WithDirty<ScheduleTemplateTaskListItemDto>
  & GenericScheduleTaskInfo
  & { isNew: boolean };
export type ScheduleTemplateCheckUsage = CheckUsageDto;
export type TaskRelation = TaskRelationDto;

export type ScheduleTemplateDetailsInfo = {
  name?: string;
};

export type ScheduleTemplateDetails = WithFormMode<WithDirtyType<WithVersion<{
  info: FormStateSnapshot<ScheduleTemplateDetailsInfo>;
  templateId?: ID;
  duration: number;
  active: ScheduleTemplateStatus;
  type: ScheduleType;
  tasks: ScheduleTemplateTask[];
}>>>;

export type ScheduleTemplateCreateDialogItem = {
  createType: ScheduleType;
  templateId?: ID;
};

export type ScheduleTemplateDialogConfig = {
  type: ScheduleType;
  helpId: HelpID;
};

export enum ScheduleTemplateAelEntityType {
  Construction = 'SC_CONSTRUCTION_TEMPLATE',
  Sales = 'SC_SALES_TEMPLATE',
  PreConstruction = 'SC_PRECONSTRUCTION_TEMPLATE',
  Maintenance = 'SC_MAINTENANCE_TEMPLATE',
}

export enum ScheduleTemplateCreateType {
  Scratch = 'Scratch',
  Clone = 'Clone',
}

export const scheduleTemplateDetailsInit = (type: ScheduleType): ScheduleTemplateDetails => ({
  info: formStateSnapshot({
    name: '',
  }),
  templateId: void 0,
  duration: 0,
  active: ScheduleTemplateStatus.Active,
  type,
  tasks: [],
  formMode: FormMode.EditMode,
});

export const startDate = (): Date => newTime(START_HOUR, 0);
export const endDate = (days?: number): Date => shiftDays(shiftHours(startDate(), DAY_HOURS), days ? days - 1 : 0);

export function createFromCostCenterFinderList(taskNumber: number): any {
  return (costCenter: CostCenterFinderListItem): ScheduleTemplateTask => {
    const supplier = ({ suppliers }: CostCenterFinderListItem) =>
      find(suppliers, ({ isPreferred }) => isPreferred);

    return {
      templateTaskId: void 0,
      name: costCenter.name,
      criticalPathId: void 0,
      type: ScheduleTaskType.CostCenter,
      resource: get(supplier(costCenter), 'name', void 0),
      supplierContactId: get(supplier(costCenter), 'id', void 0),
      duration: (costCenter.scheduledDays ? costCenter.scheduledDays : 1) * DAY_HOURS,
      taskNumber,
      changeDateAfterComplete: ScheduleTaskChangeDateAfterCompleteType.FinanceApproved,
      isSlab: false,
      isFromCriticalPath: false,
      isInSupervisorSchedule: true,
      isExcludeFromGanttChart: false,
      isResourceInUse: false,
      notes: [],
      todo: [],
      mayHaveResource: true,
      description: void 0,
      costCenterId: costCenter.id,
      predecessors: [],
      parentTaskNumber: void 0,
      startDate: startDate(),
      endDate: endDate(costCenter.scheduledDays),
      isNew: true,
      isDirty: true,
      canBeDeleted: true,
      hasChildren: false,
      canChangeCompletionPct: true,
    };
  };
}

export const scheduleTemplateDetailsFromDto = (dto: ScheduleTemplateDetailsDto): ScheduleTemplateDetails => ({
  info: formStateSnapshot(pick(dto, ['name'])),
  templateId: dto.templateId,
  duration: dto.duration,
  active: dto.active,
  type: dto.type,
  // @ts-ignore
  tasks: <ScheduleTemplateTask[]>recalculateScheduleTasks(map(
    dto.tasks,
    (task: ScheduleTemplateTask) => ({
      ...task,
      isNew: false,
      canBeDeleted: true,
      hasChildren: false,
      canChangeCompletionPct: true,
    }))),
  formMode: FormMode.ViewMode,
  version: dto.version,
});

export const scheduleTemplateDetailsToDto = (details: ScheduleTemplateDetails): ScheduleTemplateDetailsDto => ({
  ...getFormStateSnapshotValue(details.info),
  ...<ScheduleTemplateDetailsDto>omit(details, ['info', 'formMode']),
  tasks: map(details.tasks, (task: ScheduleTemplateTask) =>
    <ScheduleTemplateTask>omit(task, [
      'canBeDeleted', 'hasChildren', 'isDirty', 'completionPct', 'isNew', 'canChangeCompletionPct', 'mayHaveResource',
    ])),
});

export type ScheduleTemplateDetailsSaveFlowState =
  FormFlowState<ScheduleTemplateDetails>
  & { saveResponse?: ScheduleTemplateDetails };

export const SCHEDULE_TEMPLATE_INFO_ENTITY_CONFIG: EntityConfig<ScheduleTemplateDetailsInfo> = {
  name: { required: true },
};
export const validateScheduleTemplateInfo = entityValidatorByEntityConfig(SCHEDULE_TEMPLATE_INFO_ENTITY_CONFIG);

export const START_TASK_NUMBER = 1;
